import { NgClass } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbTimeAdapter, NgbTimepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateDirective, TranslateService } from '@ngx-translate/core';

import { SasDynamicFormService } from '@shared/sas-dynamic-form/sas-dynamic-form.service';
import { SasLabelValueComponent } from '@shared/sas-label-value/sas-label-value.component';
import { CustomTimeAdapterService } from '@shared/services/custom-time-adapter.service';
import { FormSubmittedService } from '@shared/services/form-submitted.service';

@Component({
    selector: 'sas-timepicker-field-type',
    standalone: true,
    imports: [ReactiveFormsModule, FormlyModule, NgbTimepickerModule, NgClass, TranslateDirective, SasLabelValueComponent],
    providers: [{ provide: NgbTimeAdapter, useClass: CustomTimeAdapterService }],
    template: `
        @if (isShowingSummaryInfo) {
            <sas-label-value [label]="field.props.label ?? ''"
                             [model]="formattedTime"
                             sasClass="textmode toplabel mb-2">
            </sas-label-value>
        } @else {
            <div class="form-group mb-3">
                <label [for]="field.id"
                       [id]="field.id + 'Label'"
                       class="form-label"
                       [textContent]="field.props.label"
                       translate="{{field.props.label}}">
                </label>
                @if (field.props.required) {
                <span> *</span>
                }
                <div [title]="helpText" class="sas-inline-block">
                    @if (helpText.length && !isReadonly) {
                    <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                    }
                </div>
                <ngb-timepicker [formControl]="formControl"
                                [readonlyInputs]="isReadonly!"
                                [spinners]="!isReadonly!"
                                [ngClass]="{'is-invalid' : field.formControl.invalid && (field.formControl.dirty || field.formControl.touched || formSubmittedService.isSubmitted())}"
                                required="{{field.props.required}}" />
                <div [id]="field.id + 'Desc'" role="alert" aria-live="assertive" aria-atomic="true" class="invalid-feedback">
                    <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
                </div>
            </div>
        }
    `,
})
export class SasTimepickerFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    private translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);

    helpText: string = '';
    isShowingSummaryInfo: boolean = false;

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    get formattedTime(): string {
        const { value } = this.formControl;
        if (!value) return '';

        const date = new Date(value);
        if (isNaN(date.getTime())) return value;

        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        this.isShowingSummaryInfo = this.field.props['isShowingSummaryInfo'] ?? false;
    }
}
